import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, updateDoc, doc, getDoc, writeBatch,
  serverTimestamp, increment,
} from 'firebase/firestore';
import { db, auth } from '../firebase/firebase';
import { notifyUser } from './notificationService';

export interface ChatSession {
  id: string;
  userId: string;
  userName?: string;
  userEmail?: string;
  status: 'waiting' | 'active' | 'closed' | string;
  assignedAgent?: string;
  agentName?: string;
  lastMessage?: string;
  lastMessageAt?: any;
  unreadCount?: number;
  orderId?: string;
  orderCode?: string;
  createdAt?: any;
}

export interface ChatMessage {
  id: string;
  text?: string;
  senderId?: string;
  senderName?: string;
  senderType: 'user' | 'support' | 'system' | string;
  messageType?: string;
  read?: boolean;
  timestamp?: any;
  imageUrl?: string;
}

const SESSIONS = 'supportChats';
const msgsRef = (sessionId: string) => collection(db, SESSIONS, sessionId, 'messages');

type Unsub = () => void;

function mapSessions(snap: any): ChatSession[] {
  return snap.docs.map((d: any) => ({ id: d.id, ...(d.data() as any) })) as ChatSession[];
}

// Active + waiting sessions, newest first. Falls back to client-side sort/filter
// if the composite index is missing.
export function watchActiveSessions(cb: (sessions: ChatSession[]) => void): Unsub {
  const col = collection(db, SESSIONS);
  try {
    const q = query(col, where('status', 'in', ['waiting', 'active']), orderBy('lastMessageAt', 'desc'));
    return onSnapshot(q, snap => cb(mapSessions(snap)), err => {
      console.warn('watchActiveSessions indexed query failed, falling back:', err?.message);
      return fallbackActive(col, cb);
    });
  } catch (e) {
    return fallbackActive(col, cb);
  }
}

function fallbackActive(col: any, cb: (s: ChatSession[]) => void): Unsub {
  return onSnapshot(col, (snap: any) => {
    const list = mapSessions(snap)
      .filter(s => s.status === 'waiting' || s.status === 'active')
      .sort((a, b) => msOf(b.lastMessageAt) - msOf(a.lastMessageAt));
    cb(list);
  });
}

export function watchClosedSessions(cb: (sessions: ChatSession[]) => void): Unsub {
  const col = collection(db, SESSIONS);
  try {
    const q = query(col, where('status', '==', 'closed'), orderBy('lastMessageAt', 'desc'));
    return onSnapshot(q, snap => cb(mapSessions(snap)), err => {
      console.warn('watchClosedSessions indexed query failed, falling back:', err?.message);
      return onSnapshot(col, (s: any) =>
        cb(mapSessions(s).filter(x => x.status === 'closed').sort((a, b) => msOf(b.lastMessageAt) - msOf(a.lastMessageAt))));
    });
  } catch (e) {
    return onSnapshot(col, (s: any) =>
      cb(mapSessions(s).filter(x => x.status === 'closed').sort((a, b) => msOf(b.lastMessageAt) - msOf(a.lastMessageAt))));
  }
}

export function watchMessages(sessionId: string, cb: (msgs: ChatMessage[]) => void): Unsub {
  const q = query(msgsRef(sessionId), orderBy('timestamp', 'asc'));
  return onSnapshot(q, snap => {
    cb(snap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as ChatMessage[]);
  }, err => console.warn('watchMessages failed:', err?.message));
}

export async function markMessagesAsRead(sessionId: string, messages: ChatMessage[]): Promise<void> {
  const unread = messages.filter(m => m.senderType !== 'support' && !m.read && m.id);
  if (unread.length === 0) return;
  try {
    const batch = writeBatch(db);
    unread.forEach(m => batch.update(doc(db, SESSIONS, sessionId, 'messages', m.id), { read: true }));
    await batch.commit();
  } catch (e) {
    console.warn('markMessagesAsRead failed:', e);
  }
}

async function resolveAgent(): Promise<{ uid: string; name: string }> {
  const u = auth.currentUser;
  const uid = u?.uid || 'admin';
  let name = u?.displayName || 'Support Agent';
  try {
    if (u?.uid) {
      const snap = await getDoc(doc(db, 'zapee_share_holders', u.uid));
      const n = snap.exists() ? (snap.data() as any)?.name : null;
      if (n) name = n;
    }
  } catch { /* ignore */ }
  return { uid, name };
}

export async function sendAgentMessage(sessionId: string, text: string, session: ChatSession): Promise<void> {
  const body = text.trim();
  if (!body) return;
  const { uid, name } = await resolveAgent();

  await addDoc(msgsRef(sessionId), {
    text: body,
    senderId: uid,
    senderName: name,
    senderType: 'support',
    messageType: 'text',
    read: false,
    timestamp: serverTimestamp(),
  });

  await updateDoc(doc(db, SESSIONS, sessionId), {
    lastMessage: body,
    lastMessageAt: serverTimestamp(),
    status: 'active',
    assignedAgent: uid,
    agentName: name,
    unreadCount: increment(1),
  });

  // Best-effort push to the customer; never block/fail the send.
  try {
    if (session.userId) {
      await notifyUser(session.userId, { title: 'Zapee Support', body, screen: 'chat' });
    }
  } catch (e) {
    console.warn('chat FCM push skipped:', e);
  }
}

export async function closeChatSession(sessionId: string): Promise<void> {
  await updateDoc(doc(db, SESSIONS, sessionId), {
    status: 'closed',
    closedAt: serverTimestamp(),
  });
  await addDoc(msgsRef(sessionId), {
    text: 'Chat session closed by Support Agent. Thank you!',
    senderId: 'system',
    senderName: 'System',
    senderType: 'system',
    messageType: 'system',
    timestamp: serverTimestamp(),
  });
}

export function msOf(ts: any): number {
  try {
    if (!ts) return 0;
    if (typeof ts?.toMillis === 'function') return ts.toMillis();
    if (typeof ts?.seconds === 'number') return ts.seconds * 1000;
    if (ts instanceof Date) return ts.getTime();
    const t = Date.parse(String(ts));
    return isNaN(t) ? 0 : t;
  } catch { return 0; }
}
